import { AgentGatewayMessage } from '../../../../types/AgentMessage';
import { ApprovalRequestState } from '../../../../types/ApprovalRequestState';
import { AssignmentGatewayMessage } from '../../../../types/AssignmentGatewayMessage';
import type { KeyPortMessage } from '../../../../types/KeyPortMessage';
import { PageMessage } from '../../../../types/PageMessage';
import { PriceCompareMessage } from '../../../../types/PriceCompareMessage';
import { PricesMessage } from '../../../../types/PricesMessage';
import { ProductContext } from '../../../../types/ProductContext';
import { ProductMessage } from '../../../../types/ProductMessage';
import { RequestForQuoteGatewayMessage } from '../../../../types/RequestForQuoteGatewayMessage';
import { RequestForQuoteViewFilterMessage } from '../../../../types/RequestForQuoteViewFilterMessage';
import { RequestForQuoteViewGatewayMessage } from '../../../../types/RequestForQuoteViewGatewayMessage';
import { ScheduleMessage } from '../../../../types/ScheduleMessage';
import { SpecificReportMessage } from '../../../../types/SpecificReportMessage';
import { SurchargeGatewayMessage } from '../../../../types/SurchargeGatewayMessage';
import { UserGatewayMessage } from '../../../../types/UserGatewayMessage';
import { UserStatus } from '../../../../types/UserStatus';
import type { VesselMessage } from '../../../../types/VesselMessage';
import { VesselProductsResponse } from '../../../../types/VesselProductMessage';
import { WebhookGatewayMessage } from '../../../../types/WebhookMessages';
import { Offer } from '../../../offer/model/offer.model';
import { OfferModel } from '../../../offer/model/OfferModel';
import { Order } from '../../../order/model/order.model';
import { OrderModel } from '../../../order/model/OrderModel';
import { Item } from '../../models/item.model';
import { Samplekit } from '../../models/samplekit.model';
import { authorizedGatewayAxiosInstance } from '../instances/AuthorizedGatewayAxiosInstance';
import { nonEmpty } from '../paramUtils';

export interface GetPricesParams {
    customerIds: string[];
    portIds: string[];
    items: unknown;
    vesselId?: string;
    contractIds?: string[];
    deliveryDate?: string;
}

export const getPrices = async ({
    customerIds,
    contractIds,
    deliveryDate,
    items,
    portIds,
    vesselId,
}: GetPricesParams): Promise<PricesMessage> => {
    const data = {
        items,
        portIds,
        customerIds,
        vesselId,
        contractIds,
        deliveryDate,
    };

    const response = await authorizedGatewayAxiosInstance.post('/v1/lubes-price-compare/items', data);

    return response.data;
};
export const getVessel = async (id: string): Promise<VesselMessage> => {
    const response = await authorizedGatewayAxiosInstance.get(`/v1/vessel/${id}`);

    return response.data;
};

export interface GetPaginatedVesselsParams {
    page: number;
    searchQuery?: string;
    customerIds?: string | string[];
    defaultSupplierIds?: string[];
    active?: boolean;
}

export const getPaginatedVessels = async ({
    page,
    searchQuery,
    customerIds,
    defaultSupplierIds,
    active,
}: GetPaginatedVesselsParams): Promise<PageMessage<VesselMessage>> => {
    const response = await authorizedGatewayAxiosInstance.get('/v1/vessel', {
        params: {
            active,
            page,
            searchQuery: searchQuery?.trim() || undefined,
            customerIds,
            defaultSupplierIds: nonEmpty(defaultSupplierIds),
        },
    });

    return response.data;
};

interface OrdersTotalMessage {
    count: number;
}

interface GetOrdersTotalParams {
    vesselId?: string;
    searchQuery?: string;
    read?: boolean;
    customerIds?: string[] | string;
    supplierIds?: string[] | string;
    offerStates: string[] | string;
    states: string[] | string;
    approvalRequestStates?: ApprovalRequestState[] | ApprovalRequestState;
}

export const getOrdersTotal = async ({
    vesselId,
    searchQuery,
    read,
    customerIds,
    supplierIds,
    offerStates,
    approvalRequestStates,
    states,
}: GetOrdersTotalParams): Promise<OrdersTotalMessage> => {
    const response = await authorizedGatewayAxiosInstance.get('/v2/orders/count', {
        params: {
            searchQuery: searchQuery?.trim() || undefined,
            vesselIds: vesselId,
            read,
            customerIds: nonEmpty(customerIds),
            supplierIds: nonEmpty(supplierIds),
            offerStates: nonEmpty(offerStates),
            types: ProductContext.LUBES,
            states: nonEmpty(states),
            approvalRequestStates: nonEmpty(approvalRequestStates),
        },
    });

    return response.data;
};

export interface GetOrdersParams {
    page?: number;
    vesselId?: string;
    searchQuery?: string;
    read?: boolean;
    customerIds?: string[] | string;
    supplierIds?: string[] | string;
    offerStates?: string[] | string;
    states?: string[] | string;
    approvalRequestStates?: ApprovalRequestState[] | ApprovalRequestState;
    sortField?: string;
    sortDirection?: string;
}

export const getOrders = async ({
    page,
    vesselId,
    searchQuery,
    read,
    customerIds,
    supplierIds,
    offerStates,
    states,
    approvalRequestStates,
    sortField,
    sortDirection,
}: GetOrdersParams): Promise<PageMessage<OrderModel>> => {
    const response = await authorizedGatewayAxiosInstance.get('/v2/orders', {
        params: {
            page,
            sortField: nonEmpty(sortField),
            sortDirection: nonEmpty(sortDirection),
            searchQuery: searchQuery?.trim() || undefined,
            vesselIds: vesselId,
            read,
            customerIds: nonEmpty(customerIds),
            supplierIds: nonEmpty(supplierIds),
            offerStates: nonEmpty(offerStates),
            types: ProductContext.LUBES,
            states: nonEmpty(states),
            approvalRequestStates: nonEmpty(approvalRequestStates),
        },
    });

    return {
        ...response.data,
        content: response.data.content.map((order: object) => new Order(order)),
    };
};
export const getOrder = async (orderId: string): Promise<any> => {
    const response = await authorizedGatewayAxiosInstance.get(`/v2/orders/${orderId}`);

    return new Order(response.data);
};

interface GetOffersTotalParams {
    vesselId?: string;
    searchQuery?: string;
    supplierRead?: boolean;
    customerIds?: string[] | string;
    supplierIds?: string[] | string;
    states: string[] | string;
}

interface OffersTotal {
    count: number;
}

export const getOffersTotal = async ({
    vesselId,
    searchQuery,
    supplierRead,
    supplierIds,
    customerIds,
    states,
}: GetOffersTotalParams): Promise<OffersTotal> => {
    const response = await authorizedGatewayAxiosInstance.get('/v2/offers/count', {
        params: {
            searchQuery: searchQuery?.trim() || undefined,
            vesselIds: vesselId,
            supplierIds: nonEmpty(supplierIds),
            customerIds: nonEmpty(customerIds),
            types: ProductContext.LUBES,
            states: nonEmpty(states),
            supplierRead,
        },
    });

    return response.data;
};

export interface GetOffersParams {
    page?: number;
    vesselId?: string;
    searchQuery?: string;
    supplierRead?: boolean;
    supplierIds?: string[] | string;
    customerIds?: string[] | string;
    states?: string[] | string;
    sortField?: string;
    sortDirection?: string;
}

export const getOffers = async ({
    page,
    vesselId,
    searchQuery,
    supplierRead,
    supplierIds,
    customerIds,
    states,
    sortField,
    sortDirection,
}: GetOffersParams): Promise<PageMessage<OfferModel>> => {
    const response = await authorizedGatewayAxiosInstance.get('/v2/offers', {
        params: {
            page: page ?? undefined,
            sortField: nonEmpty(sortField),
            sortDirection: nonEmpty(sortDirection),
            searchQuery: searchQuery?.trim() || undefined,
            vesselIds: vesselId,
            supplierIds: nonEmpty(supplierIds),
            customerIds: nonEmpty(customerIds),
            types: ProductContext.LUBES,
            states: nonEmpty(states),
            supplierRead,
        },
    });

    return {
        ...response.data,
        content: response.data.content.map((offer: unknown) => new Offer(offer)),
    };
};

export interface GetOfferListParams {
    orderIds?: string[] | string;
}

export const getOfferList = async ({ orderIds }: GetOfferListParams): Promise<OfferModel[]> => {
    const response = await authorizedGatewayAxiosInstance.get('/v2/offers/list', {
        params: {
            orderIds: nonEmpty(orderIds),
            types: ProductContext.LUBES,
        },
    });

    return response.data.content.map((offer: unknown) => new Offer(offer));
};
export const getOfferById = async (offerId: string): Promise<OfferModel> => {
    const response = await authorizedGatewayAxiosInstance.get(`/v2/offers/${offerId}`);

    // @ts-ignore
    return new Offer(response.data);
};

export const getOfferHistory = async (offerId: string): Promise<OfferModel[]> => {
    const response = await authorizedGatewayAxiosInstance.get(`/v2/offers/history/${offerId}`);

    return response.data.content.map((offer: unknown) => new Offer(offer));
};

export const getMessagesForOfferId = async (offerId: string): Promise<any> => {
    const response = await authorizedGatewayAxiosInstance.get(`/v1/message/offer/${offerId}`);

    return response.data;
};

export interface GetAssignmentListParams {
    customerIds?: string[];
    supplierIds?: string[];
}

export const getAssignmentList = async ({
    customerIds,
    supplierIds,
}: GetAssignmentListParams): Promise<AssignmentGatewayMessage[]> => {
    const response = await authorizedGatewayAxiosInstance.get('/v1/assignment/list', {
        params: {
            customerIds: nonEmpty(customerIds),
            supplierIds: nonEmpty(supplierIds),
        },
    });

    return response.data;
};

export interface GetAssignmentsParams {
    page: number;
    customerIds?: string[];
    supplierIds?: string[];
}

export const getAssignments = async ({
    page,
    customerIds,
    supplierIds,
}: GetAssignmentsParams): Promise<AssignmentGatewayMessage> => {
    const response = await authorizedGatewayAxiosInstance.get('/v1/assignment', {
        params: {
            page,
            customerIds: customerIds?.length ? customerIds : undefined,
            supplierIds: supplierIds?.length ? supplierIds : undefined,
        },
    });

    return response.data;
};

export const getVesselTanksByVesselId = async (vesselId: string): Promise<any> => {
    const response = await authorizedGatewayAxiosInstance.get('/v1/vessel-tanks', {
        params: { vesselIds: vesselId },
    });

    return response.data;
};

export interface GetAgentsParams {
    page: number;
    active: boolean;
    portIds?: string[];
    customerIds?: string[];
    searchQuery?: string;
}

export const getAgents = async ({
    portIds = [],
    customerIds = [],
    searchQuery,
    page = 0,
    active,
}: GetAgentsParams): Promise<PageMessage<AgentGatewayMessage>> => {
    const response = await authorizedGatewayAxiosInstance.get('/v1/agents/page', {
        params: {
            page,
            active,
            searchQuery: searchQuery?.trim() || undefined,
            portIds: portIds.length ? portIds : undefined,
            customerIds: customerIds.length ? customerIds : undefined,
        },
    });

    return response.data;
};

export const getAgent = async (agentId: string): Promise<AgentGatewayMessage> => {
    const response = await authorizedGatewayAxiosInstance.get(`/v1/agents/${agentId}`);

    return response.data;
};

export const getLatestPorts = async (): Promise<any> => {
    const response = await authorizedGatewayAxiosInstance.get('/v1/port/latest');

    return response.data;
};

export interface GetKeyPortsParams {
    page: number;
    searchQuery?: string;
    customerGroupIds?: string[];
}

export const getKeyPorts = async ({
    page = 0,
    searchQuery,
    customerGroupIds = [],
}: GetKeyPortsParams): Promise<PageMessage<KeyPortMessage>> => {
    const response = await authorizedGatewayAxiosInstance.get('/v1/key-ports', {
        params: {
            page,
            searchQuery: searchQuery?.trim() || undefined,
            context: ProductContext.LUBES,
            customerGroupIds: customerGroupIds.length ? customerGroupIds : undefined,
        },
    });

    return response.data;
};
export const getKeyPort = async (keyPortId: string): Promise<KeyPortMessage> => {
    const response = await authorizedGatewayAxiosInstance.get(`/v1/key-ports/${keyPortId}`);

    return response.data;
};
interface GetScheduleListParams {
    imo: string;
    etdFrom?: string;
    etdTo?: string;
    customerGroupId?: string;
}

export const getScheduleList = async ({
    imo,
    etdFrom,
    etdTo,
    customerGroupId,
}: GetScheduleListParams): Promise<ScheduleMessage[]> => {
    const response = await authorizedGatewayAxiosInstance.get('/v1/schedule/list', {
        params: {
            imo,
            etdFrom: nonEmpty(etdFrom),
            etdTo: nonEmpty(etdTo),
            customerGroupId: nonEmpty(customerGroupId),
        },
    });

    return response.data;
};
export const getSharedScheduleList = async (offerId: string): Promise<ScheduleMessage[]> => {
    const response = await authorizedGatewayAxiosInstance.get('/v1/schedule/list', {
        params: { offerId },
    });

    return response.data;
};

export const getPriceCompare = async (
    vesselId: string,
    portIds: string[]
): Promise<{ prices: PriceCompareMessage[] }> => {
    const response = await authorizedGatewayAxiosInstance.get('/v1/lubes-price-compare/vessels', {
        params: {
            vesselId,
            portIds,
        },
    });

    return response.data;
};
export const getMessages = async (offerId: string): Promise<any> => {
    const response = await authorizedGatewayAxiosInstance.get(`/v1/message/offer/${offerId}`);

    return response.data;
};
export const getInitialEnquiry = async (offerId: string): Promise<OfferModel> => {
    const response = await authorizedGatewayAxiosInstance.get(`/v2/offers/history/${offerId}/initial`);

    // @ts-ignore
    return new Offer(response.data);
};
export const getManagementUserById = async (userId: string): Promise<UserGatewayMessage> => {
    const response = await authorizedGatewayAxiosInstance.get(`/v1/user/management/${userId}`);

    return response.data;
};

export interface ManagementUsersParams {
    page: number;
    searchQuery?: string;
    companyIds?: string[];
    groupIds?: string[];
    statuses?: UserStatus[];
}

export const getManagementUsers = async ({
    page,
    searchQuery,
    companyIds,
    groupIds,
    statuses,
}: ManagementUsersParams): Promise<PageMessage<UserGatewayMessage>> => {
    const response = await authorizedGatewayAxiosInstance.get<PageMessage<UserGatewayMessage>>('/v1/user/management', {
        params: {
            page,
            searchQuery: nonEmpty(searchQuery),
            companyIds: nonEmpty(companyIds),
            groupIds: nonEmpty(groupIds),
            statuses: nonEmpty(statuses),
        },
    });

    return response.data;
};

export interface ManagementUsersListParams {
    searchQuery?: string;
    companyIds?: string[];
    groupIds?: string[];
    statuses?: UserStatus[];
}

export const getManagementUsersList = async ({
    searchQuery,
    companyIds,
    groupIds,
    statuses,
}: ManagementUsersListParams): Promise<{ content: UserGatewayMessage[] }> => {
    const response = await authorizedGatewayAxiosInstance.get<{ content: UserGatewayMessage[] }>(
        '/v1/user/management/list',
        {
            params: {
                searchQuery: nonEmpty(searchQuery),
                companyIds: nonEmpty(companyIds),
                groupIds: nonEmpty(groupIds),
                statuses: nonEmpty(statuses),
            },
        }
    );

    return response.data;
};

export interface GetReportsConfig {
    from: number;
    to: number;
    customerIds?: string[];
    supplierIds?: string[];
}

export const getVesselReports = async ({
    from,
    to,
    customerIds,
    supplierIds,
}: GetReportsConfig): Promise<SpecificReportMessage> => {
    const response = await authorizedGatewayAxiosInstance.get<SpecificReportMessage>('/v1/report/vessel', {
        params: {
            from,
            to,
            types: ProductContext.LUBES,
            customerIds: nonEmpty(customerIds),
            supplierIds: nonEmpty(supplierIds),
        },
    });

    return response.data;
};

export const getSupplierReports = async ({
    from,
    to,
    customerIds,
    supplierIds,
}: GetReportsConfig): Promise<SpecificReportMessage> => {
    const response = await authorizedGatewayAxiosInstance.get<SpecificReportMessage>('/v1/report/supplier', {
        params: {
            from,
            to,
            types: ProductContext.LUBES,
            customerIds: nonEmpty(customerIds),
            supplierIds: nonEmpty(supplierIds),
        },
    });

    return response.data;
};

export const getPortReports = async ({
    from,
    to,
    customerIds,
    supplierIds,
}: GetReportsConfig): Promise<SpecificReportMessage> => {
    const response = await authorizedGatewayAxiosInstance.get<SpecificReportMessage>('/v1/report/port', {
        params: {
            from,
            to,
            types: ProductContext.LUBES,
            customerIds: nonEmpty(customerIds),
            supplierIds: nonEmpty(supplierIds),
        },
    });

    return response.data;
};

export const getProductReports = async ({
    from,
    to,
    customerIds,
    supplierIds,
}: GetReportsConfig): Promise<SpecificReportMessage> => {
    const response = await authorizedGatewayAxiosInstance.get<SpecificReportMessage>('/v1/report/product', {
        params: {
            from,
            to,
            types: ProductContext.LUBES,
            customerIds: nonEmpty(customerIds),
            supplierIds: nonEmpty(supplierIds),
        },
    });

    return response.data;
};

export const getCustomerReports = async ({
    from,
    to,
    customerIds,
    supplierIds,
}: GetReportsConfig): Promise<SpecificReportMessage> => {
    const response = await authorizedGatewayAxiosInstance.get<SpecificReportMessage>('/v1/report/customer', {
        params: {
            from,
            to,
            types: ProductContext.LUBES,
            customerIds: nonEmpty(customerIds),
            supplierIds: nonEmpty(supplierIds),
        },
    });

    return response.data;
};

export const getSurcharges = async (supplierGroupId: string): Promise<SurchargeGatewayMessage[]> => {
    const response = await authorizedGatewayAxiosInstance.get('/v1/surcharge', {
        params: { supplierGroupId },
    });

    return response.data;
};

export const getLatestProducts = async (): Promise<ProductMessage[]> => {
    const response = await authorizedGatewayAxiosInstance.get(`/v1/product/latest`);

    return response.data;
};

export const getWebhookList = async (groupId: string): Promise<WebhookGatewayMessage[]> => {
    const response = await authorizedGatewayAxiosInstance.get('/v1/webhooks', {
        params: { groupId },
    });

    return response.data.webhooks;
};

export const getVesselProducts = async (vesselId: string): Promise<VesselProductsResponse> => {
    const response = await authorizedGatewayAxiosInstance.get(`/v2/vessels/${vesselId}/products`);

    return response.data;
};

export const getRequestForQuotesPage = async (
    filter: RequestForQuoteViewFilterMessage
): Promise<PageMessage<RequestForQuoteViewGatewayMessage>> => {
    const response = await authorizedGatewayAxiosInstance.post('/v1/request-for-quotes/view/page', {
        ...filter,
        types: [ProductContext.LUBES],
    });

    return response.data;
};

export const getRequestsForQuotesCount = async (filter: RequestForQuoteViewFilterMessage): Promise<number> => {
    const response = await authorizedGatewayAxiosInstance.post('/v1/request-for-quotes/view/count', {
        ...filter,
        types: [ProductContext.LUBES],
    });

    return response.data;
};

export const getRequestForQuote = async (id: string): Promise<RequestForQuoteGatewayMessage> => {
    const response = await authorizedGatewayAxiosInstance.get(`/v1/request-for-quotes/${id}`);
    return {
        ...response.data,
        samplekits: response.data.samplekits?.length
            ? response.data.samplekits.map((kit) => new Samplekit(kit))
            : undefined,
        items: response.data.items?.length ? response.data.items.map((item) => new Item(item)) : undefined,
        offers: response.data.offers.map((offer) => new Offer(offer)),
    };
};
